/**
 * Where a notification click lands, in one place.
 *
 * The bell tray, the toast, and the inbox grid all open a notification the same
 * way: go to the record the notification is about, and mark the receipt read.
 * Keeping the destination rule here means those three surfaces cannot drift.
 */

export const NOTIFICATIONS_LIST_URL = "/notifications";

/**
 * Where a reader goes to change what reaches them.
 *
 * Tenant-scoped, unlike the inbox: preferences are stored per organization, so the
 * same person has different settings in each one and the route has to say which.
 * Built here beside the inbox URL so the tray and the left nav cannot disagree
 * about where the page lives.
 */
export function notificationPreferencesUrl(tenantId: string): string {
  return `/o/${tenantId}/settings/notifications`;
}

/**
 * The fields a click needs, typed structurally so the tray rows, the toast's
 * freshly fetched notification, and the inbox grid rows all satisfy it without
 * a cast.
 */
export type NotificationOpenTarget = {
  id: string;
  readAt?: string | null;
  notification: {
    subject?: { route?: string | null } | null;
  };
};

/**
 * Notifications without a subject — or whose subject was deleted or has no page
 * of its own — fall back to their own detail page rather than the inbox.
 *
 * Landing on the inbox would read as a dead click: the reader is already looking
 * at that list, and the grid shows a title but not the body, so the one thing
 * they wanted stays out of reach. Some kinds carry everything actionable in the
 * body precisely because they have nowhere to point — a background job has no
 * customer-facing record, and staff-composed announcements have no subject at
 * all. The detail page stays de-emphasized for everything else: whenever a
 * subject route exists, the click goes to the record, never here.
 */
export function getNotificationDestination(target: NotificationOpenTarget) {
  return (
    target.notification.subject?.route ??
    `${NOTIFICATIONS_LIST_URL}/${encodeURIComponent(target.id)}`
  );
}
